import { IStyles, pxToRem, pxTovW } from '@geneo2-web/shared-ui';
import {
  Box,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Table,
  TableContainer,
  Typography,
} from '@mui/material';
import { ProctoringMetricsModel } from '@protos/learning_management/lms.db_pb';
import { formatDateTimeMismatch } from '../../CompletedHomework/components/TimeMismatchButton';

const styles: IStyles = {
  root: {
    marginTop: { xs: pxToRem(20), md: pxTovW(24) },
    marginBottom: { xs: pxToRem(20), md: pxTovW(0) },
    paddingX: { xs: pxToRem(10), md: pxTovW(0) },
  },
  headerCell: {
    width: 'max-content',
    paddingX: { xs: pxToRem(5), md: pxTovW(10) },
    paddingY: { xs: pxToRem(5), md: pxTovW(14) },
    borderBottom: '1px solid #007CDC',
  },
  headerCellTypo: {
    fontSize: { xs: pxToRem(14), md: pxTovW(16) },
    whiteSpace: 'nowrap',
  },
  bodyCell: {
    paddingX: { xs: pxToRem(5), md: pxTovW(10) },
    paddingY: { xs: pxToRem(5), md: pxTovW(14) },
  },
  bodyCellTypo: {
    fontSize: { xs: pxToRem(14), md: pxTovW(14) },
  },
};

interface IProps {
  proctoringMetrics: ProctoringMetricsModel;
}

export const ProctoringDataTable = ({ proctoringMetrics }: IProps) => {
  const tableRows = transformProctoringMetrics(proctoringMetrics);

  if (tableRows.length === 0) return null;

  return (
    <Box sx={styles.root}>
      <Typography variant="h2">Proctoring details</Typography>
      <TableContainer sx={{ marginTop: { xs: pxToRem(10), md: pxTovW(12) } }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f4f9fd' }}>
              {HEADERS.map((data, index) => (
                <TableCell key={index} sx={data.cellStyle}>
                  <Typography
                    textAlign={data.name === 'NAME' ? 'left' : 'center'}
                    variant="h2"
                    sx={styles.headerCellTypo}
                  >
                    {data.name}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {tableRows.map((row, index) => (
              <TableRow
                key={row.srNo}
                sx={{
                  backgroundColor: index % 2 === 1 ? '#f4f9fd' : 'white',
                }}
              >
                <TableCell sx={styles.bodyCell}>
                  <Typography textAlign="center" sx={styles.bodyCellTypo}>
                    {row.srNo}
                  </Typography>
                </TableCell>
                <TableCell sx={styles.bodyCell}>
                  <Typography sx={styles.bodyCellTypo}>{row.name}</Typography>
                </TableCell>
                <TableCell sx={styles.bodyCell}>
                  <Typography textAlign="center" sx={styles.bodyCellTypo}>
                    {row.count}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// -------------------------------------------------------------

const HEADERS = [
  { name: 'SR. NO', cellStyle: styles.headerCell },
  {
    name: 'NAME',
    cellStyle: { ...styles.headerCell, width: { xs: '70%', md: pxTovW(300) } },
  },
  { name: 'COUNT', cellStyle: styles.headerCell },
  // { name: 'VALUE', cellStyle: styles.headerCell },
];

// -------------------------------------------------------------

interface ProctoringRow {
  srNo: number;
  name: string;
  count: number | string;
}

const transformProctoringMetrics = (
  metrics: ProctoringMetricsModel
): ProctoringRow[] => {
  const rows: ProctoringRow[] = [];
  let srNo = 1;

  // Full Screen Exits
  if (metrics.fullScreenExits && metrics.fullScreenExits.length > 0) {
    rows.push({
      srNo: srNo++,
      name: 'Full Screen Exits',
      count: metrics.fullScreenExits.length,
    });
  }

  // Tab/Window Switch
  if (metrics.tabSwitches && metrics.tabSwitches.length > 0) {
    rows.push({
      srNo: srNo++,
      name: 'Tab/Window Switch',
      count: metrics.tabSwitches.length,
    });
  }

  // Copy/Paste/Keyboard Shortcuts attempts
  const copyCount = metrics.copyAttempts?.length || 0;
  const timeMismatches = metrics.timeMismatches || [];
  const pasteCount = metrics.pasteAttempts?.length || 0;
  const blockedShortcutsCount = metrics.blockedShortcuts?.length || 0;
  const totalAttempts = copyCount + pasteCount + blockedShortcutsCount;

  if (totalAttempts > 0) {
    rows.push({
      srNo: srNo++,
      name: 'Attempt to try copy paste or keyboard shortcuts',
      count: totalAttempts,
    });
  }

  // Right Click Attempts
  if (metrics.rightClickAttempts && metrics.rightClickAttempts.length > 0) {
    rows.push({
      srNo: srNo++,
      name: 'Right Click Attempts',
      count: metrics.rightClickAttempts.length,
    });
  }

  // Multiple Sessions
  if (metrics.multipleSessions && metrics.multipleSessions.length > 0) {
    rows.push({
      srNo: srNo++,
      name: 'Multiple Sessions',
      count: metrics.multipleSessions.length,
    });
  }

  // Idle Time Data
  if (metrics.idleTimeData && metrics.idleTimeData.length > 0) {
    rows.push({
      srNo: srNo++,
      name: 'Idle Time Periods',
      count: metrics.idleTimeData.length,
    });
  }

  if (timeMismatches.length > 0) {
    rows.push({
      srNo: srNo++,
      name: 'Time Mismatches',
      count: timeMismatches
        .map((val) => formatDateTimeMismatch(val.timestamp))
        .join(' | '),
    });
  }

  return rows;
};

// -------------------------------------------------------------
